import { useState } from 'react';
import axios from 'axios';
import { homeForRole } from '@/utils/homeForRole';
import { safePostLoginPath } from '@/utils/loginReturnPath';
import { detectRole } from '../loginRoleChrome';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?\d[\d\s-]{8,}$/;

export function isValidLoginIdentifier(value) {
  const v = (value || '').trim();
  return EMAIL_RE.test(v) || PHONE_RE.test(v.replace(/\s/g, ''));
}

export function useLoginAuth({ slug, setUser, forceOtpLogin = false }) {
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpChallenge, setOtpChallenge] = useState(null);

  const authOpts = () => ({
    withCredentials: true,
    headers: { 'X-Tenant-Slug': slug },
  });

  const goHome = (data) => {
    setUser(data);
    const next = safePostLoginPath(window.location.search);
    window.location.assign(next || homeForRole(data.role, data));
  };

  const requestOtpLogin = async () => {
    if (!isValidLoginIdentifier(userId)) {
      setError('Enter a valid email address (e.g. fic.hospital@iith.ac.in) or mobile number.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/login`, {
        email: userId.trim().toLowerCase(),
      }, authOpts());
      if (res.data?.requires_otp && res.data?.challenge_id) {
        setOtpChallenge(res.data);
        setLoading(false);
        return;
      }
      setError('OTP could not be issued. Please try again.');
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (status === 401) setError('No account found for this email or phone.');
      else if (status === 429) setError('Too many OTP requests. Please wait and try again.');
      else if (!err.response) setError('Cannot reach server. Please try again shortly.');
      else setError('Could not start sign-in. Please try again.');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!slug) {
      setError('Please select your clinic / tenant first.');
      return;
    }
    if (forceOtpLogin) {
      await requestOtpLogin();
      return;
    }

    setLoading(true);
    const role = detectRole(userId);
    try {
      let data;
      if (role === 'email') {
        setLoading(false);
        await requestOtpLogin();
        return;
      }
      if (role === 'doctor') {
        const res = await axios.post(`${API_URL}/api/auth/doctor-login`, {
          doctor_id: userId.trim(),
          password: password || 'any',
        }, authOpts());
        data = res.data;
      } else {
        const res = await axios.post(`${API_URL}/api/auth/staff-login`, {
          user_id: userId.trim(),
          role,
          password,
        }, authOpts());
        data = res.data;
      }
      goHome(data);
    } catch (err) {
      let msg = 'Login failed. Please check your credentials.';
      if (!err.response) msg = 'Cannot reach server. Check your network or try again shortly.';
      else if (err.response.status === 401) msg = 'Invalid credentials.';
      else if (typeof err.response.data?.detail === 'string') msg = err.response.data.detail;
      setError(msg);
      setLoading(false);
    }
  };

  const handleOtpVerify = async (otp) => {
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/auth/login/verify-otp`,
        { challenge_id: otpChallenge.challenge_id, otp },
        authOpts(),
      );
      goHome(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'OTP verification failed.');
      setLoading(false);
    }
  };

  return {
    userId, setUserId, password, setPassword, error, setError,
    loading, otpChallenge, setOtpChallenge, handleSubmit, handleOtpVerify,
  };
}
